/**
 * Helpers for SSG salon document uploads (document_key slug + chart pipeline filter).
 */

/** Medical charts use chart-{clientId}-{ts}-data / -render; hide from general documents list */
export function isChartPipelineDocumentKey(key: string): boolean {
  return /^chart-.+-(data|render)$/.test(key);
}

export function extensionFromFileName(name: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(name.trim());
  return (m?.[1] || "").toLowerCase();
}

/**
 * True when the first bytes of `blob` do not match the extension implied by `fileName`
 * (e.g. API returned JSON, HTML, or base64 text saved as `.jpg`).
 */
export async function blobExtensionMagicMismatch(
  blob: Blob,
  fileName: string,
): Promise<boolean> {
  const ext = extensionFromFileName(fileName);
  if (!ext) return false;
  const n = Math.min(blob.size, 16);
  if (n < 4) {
    return (
      ext === "jpg" ||
      ext === "jpeg" ||
      ext === "png" ||
      ext === "pdf" ||
      ext === "gif"
    );
  }
  const buf = await new Response(blob.slice(0, n)).arrayBuffer();
  const u = new Uint8Array(buf);
  switch (ext) {
    case "jpg":
    case "jpeg":
      return !(u[0] === 0xff && u[1] === 0xd8 && u[2] === 0xff);
    case "png":
      return !(
        u[0] === 0x89 &&
        u[1] === 0x50 &&
        u[2] === 0x4e &&
        u[3] === 0x47
      );
    case "gif":
      return !(u[0] === 0x47 && u[1] === 0x49 && u[2] === 0x46);
    case "webp":
      if (n < 12) return false;
      return !(
        u[0] === 0x52 &&
        u[1] === 0x49 &&
        u[2] === 0x46 &&
        u[3] === 0x46 &&
        u[8] === 0x57 &&
        u[9] === 0x45 &&
        u[10] === 0x42 &&
        u[11] === 0x50
      );
    case "pdf":
      return !(
        u[0] === 0x25 &&
        u[1] === 0x50 &&
        u[2] === 0x44 &&
        u[3] === 0x46
      );
    case "docx":
    case "xlsx":
    case "xlsm":
    case "xlsb":
    case "xltx":
      return !(u[0] === 0x50 && u[1] === 0x4b);
    case "xls":
      return !(
        (u[0] === 0xd0 && u[1] === 0xcf && u[2] === 0x11 && u[3] === 0xe0) ||
        (u[0] === 0x50 && u[1] === 0x4b)
      );
    default:
      return false;
  }
}

/** Salon-wide document uploads (no clientId) — SSG allowlist */
export const SALON_WIDE_DOCUMENT_ALLOWED_EXT = new Set([
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "webp",
  "gif",
]);

/**
 * Client-scoped / PHI uploads — SSG allowlist (must not use webp/gif here per API rules).
 */
export const PHI_CLIENT_DOCUMENT_ALLOWED_EXT = new Set([
  "docx",
  "pdf",
  "jpg",
  "jpeg",
  "png",
  "xlsx",
  "xlsm",
  "xlsb",
  "xltx",
  "xls",
]);

/** @deprecated use SALON_WIDE_DOCUMENT_ALLOWED_EXT or PHI_CLIENT_DOCUMENT_ALLOWED_EXT */
export const CLIENT_DOCUMENT_ALLOWED_EXT = SALON_WIDE_DOCUMENT_ALLOWED_EXT;

export function mimeFromExtension(ext: string): string {
  switch (ext) {
    case "pdf":
      return "application/pdf";
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "webp":
      return "image/webp";
    case "gif":
      return "image/gif";
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "xlsx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    case "xlsm":
      return "application/vnd.ms-excel.sheet.macroEnabled.12";
    case "xlsb":
      return "application/vnd.ms-excel.sheet.binary.macroEnabled.12";
    case "xltx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.template";
    case "xls":
      return "application/vnd.ms-excel";
    default:
      return "application/octet-stream";
  }
}

/** Client document list: markup editor only for raster images (not PDF / Office). */
export function isMarkupableImageContentType(
  contentType: string | undefined,
): boolean {
  if (!contentType) return false;
  const c = contentType.toLowerCase();
  return (
    c === "image/jpeg" ||
    c === "image/jpg" ||
    c === "image/png" ||
    c === "image/webp" ||
    c === "image/gif"
  );
}

/** List/table label: API displayName, else document_key slug */
export function documentListDisplayName(doc: {
  displayName?: string | null;
  documentKey: string;
}): string {
  const trimmed = doc.displayName?.trim();
  return trimmed ? trimmed : doc.documentKey;
}

export type DocumentListCategory =
  | "IMAGE"
  | "PDF"
  | "SPREADSHEET"
  | "DOCUMENT"
  | "FILE";

export function documentListCategoryFromMime(
  contentType: string | undefined,
): DocumentListCategory {
  if (!contentType) return "FILE";
  const c = contentType.toLowerCase().split(";")[0].trim();
  if (c.startsWith("image/")) return "IMAGE";
  if (c.includes("pdf")) return "PDF";
  if (
    c.includes("spreadsheet") ||
    c.includes("excel") ||
    c === "application/vnd.ms-excel"
  ) {
    return "SPREADSHEET";
  }
  if (c.includes("wordprocessing") || c.includes("msword")) return "DOCUMENT";
  return "FILE";
}

export function formatDocumentByteSize(
  bytes: number | undefined | null,
): string {
  if (bytes == null || Number.isNaN(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function documentListDateShort(iso: string | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

/**
 * Who last saved the document: prefers `lastSavedByStaffName` from the list API,
 * then `uploadedByName` / metadata fallbacks.
 */
export function documentUploaderDisplayName(doc: {
  lastSavedByStaffName?: string | null;
  uploadedByName?: string | null;
  currentMetadata?: Record<string, unknown> | null;
}): string | null {
  const lastSaved = doc.lastSavedByStaffName?.trim();
  if (lastSaved) return lastSaved;
  // Accept snake_case fields from API payloads that are not transformed client-side.
  const rawDoc = doc as Record<string, unknown>;
  for (const key of [
    "last_saved_by_staff_name",
    "lastSavedBy",
    "last_saved_by",
    "createdByStaffName",
    "created_by_staff_name",
  ]) {
    const v = rawDoc[key];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  const top = doc.uploadedByName?.trim();
  if (top) return top;
  const meta = doc.currentMetadata;
  if (!meta || typeof meta !== "object") return null;
  const keys = [
    "uploadedByName",
    "uploaded_by_name",
    "uploadedBy",
    "uploaded_by",
    "uploadedByDisplayName",
    "uploaderName",
    "createdByName",
    "created_by_name",
  ];
  for (const k of keys) {
    const v = meta[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

const MIME_TO_DOWNLOAD_EXT: Record<string, string> = {
  "application/pdf": ".pdf",
  "image/png": ".png",
  "image/jpeg": ".jpg",
  "image/jpg": ".jpg",
  "image/webp": ".webp",
  "image/gif": ".gif",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    ".docx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
  "application/vnd.ms-excel.sheet.macroenabled.12": ".xlsm",
  "application/vnd.ms-excel.sheet.binary.macroenabled.12": ".xlsb",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.template":
    ".xltx",
  "application/vnd.ms-excel": ".xls",
};

/**
 * File extension (including dot) from `currentContentType` for Save / download names.
 */
export function downloadExtensionFromContentType(
  contentType: string | undefined,
): string {
  if (!contentType) return "";
  const primary = contentType.split(";")[0].trim().toLowerCase();
  return MIME_TO_DOWNLOAD_EXT[primary] || "";
}

/**
 * Safe filename for browser download (label + extension), per list row metadata.
 */
export function suggestedClientDocumentDownloadFilename(doc: {
  displayName?: string | null;
  documentKey: string;
  currentContentType?: string | null;
}): string {
  const label = documentListDisplayName(doc)
    .replace(/[/\\?%*:|"<>]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 180);
  const base = label || "document";
  const ext = downloadExtensionFromContentType(
    doc.currentContentType ?? undefined,
  );
  if (/\.\w{2,5}$/i.test(base)) return base;
  return ext ? `${base}${ext}` : base;
}

/** Card title: friendly name with extension when MIME implies one and name lacks it */
export function documentCardTitle(doc: {
  displayName?: string | null;
  documentKey: string;
  currentContentType?: string | null;
}): string {
  const base = documentListDisplayName(doc);
  const ext = downloadExtensionFromContentType(
    doc.currentContentType ?? undefined,
  );
  if (!ext) return base;
  if (/\.[a-z0-9]{2,5}$/i.test(base)) return base;
  return `${base}${ext}`;
}

const SNIFF_MAX_BYTES = 4096;

/**
 * True when the first bytes look like JSON or an HTML document — usually means an
 * error payload or login page was saved instead of a real file.
 */
export async function blobStartsLikeJsonOrHtmlDocument(
  blob: Blob,
): Promise<boolean> {
  const n = Math.min(blob.size, SNIFF_MAX_BYTES);
  if (n === 0) return true;
  const slice = blob.slice(0, n);
  const buf = await new Response(slice).arrayBuffer();
  const bytes = new Uint8Array(buf);
  let i = 0;
  while (
    i < bytes.length &&
    (bytes[i] === 9 || bytes[i] === 10 || bytes[i] === 13 || bytes[i] === 32)
  ) {
    i += 1;
  }
  if (
    i + 2 < bytes.length &&
    bytes[i] === 0xef &&
    bytes[i + 1] === 0xbb &&
    bytes[i + 2] === 0xbf
  ) {
    i += 3;
  }
  while (
    i < bytes.length &&
    (bytes[i] === 9 || bytes[i] === 10 || bytes[i] === 13 || bytes[i] === 32)
  ) {
    i += 1;
  }
  let t = "";
  for (let k = i; k < bytes.length; k += 1) {
    t += String.fromCharCode(bytes[k]);
  }
  const s = t.trimStart();
  return (
    s.startsWith("{") ||
    s.startsWith("[") ||
    s.startsWith("<!DOCTYPE") ||
    s.startsWith("<!doctype") ||
    s.startsWith("<html")
  );
}

/**
 * Stable slug for POST document_key: ^[a-z0-9][a-z0-9_-]{0,127}$
 */
export function buildDocumentKeyFromFileName(fileName: string): string {
  const baseRaw = fileName.replace(/\.[^.]+$/i, "").trim() || "document";
  let slug = baseRaw
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-+/g, "-");
  if (!slug || !/^[a-z0-9]/.test(slug)) {
    slug = `doc-${slug || "file"}`.replace(/[^a-z0-9_-]/g, "-");
  }
  const uniq = `-${Date.now().toString(36)}`;
  let key = `${slug}${uniq}`;
  if (key.length > 128) {
    key = key.slice(0, 128);
  }
  if (!/^[a-z0-9][a-z0-9_-]{0,127}$/.test(key)) {
    key = `upload-${Date.now().toString(36)}`.slice(0, 128);
  }
  return key;
}
